import * as THREE from 'three';
import { state } from '../state.ts';
import type { RenderLoop } from '../scene/renderLoop.ts';
import type { FingerHealer } from '../retarget/fingers.ts';

/**
 * analyze() — cheap numeric pre-passes over the clip so an agent (or a human)
 * knows WHERE to look before spending vision. Each scan reports frame ranges
 * plus the tool that fixes that class of defect. See docs/AUTO_CLEANUP.md.
 */

export interface FrameRange {
  startFrame: number;
  endFrame: number;
}

export interface AnalyzeReport {
  scanned: { startFrame: number; endFrame: number; stride: number };
  /** Finger joints deviating from neighbours / anatomical limits. */
  fingers: {
    actorId: string;
    side: string;
    finger: string;
    ranges: (FrameRange & { peakConfidence: number })[];
    suggestion: string;
  }[];
  /** Near-instant hand rotation jumps — mis-oriented tracker events. */
  handFlips: {
    actorId: string;
    side: string;
    frame: number;
    deltaDegrees: number;
    suggestion: string;
  }[];
  /** Feet below the floor plane. */
  ground: {
    actorId: string;
    side: string;
    minY: number;
    penetration: (FrameRange & { minY: number })[];
    suggestion: string;
  }[];
  /** Hands lingering near another actor's bone — probable intended contact. */
  contactCandidates: {
    actorId: string;
    limb: string;
    targetActorId: string;
    targetBone: string;
    startFrame: number;
    endFrame: number;
    minDistance: number;
    suggestion: string;
  }[];
}

export interface AnalyzerDeps {
  loop: RenderLoop;
  setMixersTime: (seconds: number) => void;
  /** Skeleton actors with a SOURCE-bone resolver (raw data, not puppets). */
  getSkeletonActors: () => { id: string; label: string }[];
  sourceBone: (actorId: string, canonical: string) => THREE.Object3D | null;
  getFingerHealer: () => FingerHealer | null;
}

const HAND_SIDES = ['left', 'right'] as const;
const CONTACT_TARGET_BONES = [
  'hips',
  'chest',
  'head',
  'leftshoulder',
  'rightshoulder',
  'lefthand',
  'righthand',
  'leftforearm',
  'rightforearm',
];
const FLIP_THRESHOLD_DEG = 45; // per sampled step
const GROUND_EPSILON = -0.02; // metres below the floor
const CONTACT_DISTANCE = 0.15; // metres
const CONTACT_MIN_SECONDS = 0.15;

export function createAnalyzer(deps: AnalyzerDeps) {
  function analyze(opts: { startFrame?: number; endFrame?: number } = {}): AnalyzeReport {
    const fps = state.fps;
    const start = Math.max(0, opts.startFrame ?? state.startFrame);
    const end = Math.min(state.totalFrames, opts.endFrame ?? state.endFrame);
    const stride = Math.max(1, Math.round(fps / 30)); // ~30 samples/second

    const skeletons = deps.getSkeletonActors();
    const healer = deps.getFingerHealer();

    // Trackers accumulated across the sweep.
    const fingerFrames = new Map<string, { frames: number[]; confs: number[] }>();
    const handQuats = new Map<string, THREE.Quaternion>();
    const flips: AnalyzeReport['handFlips'] = [];
    const groundAcc = new Map<string, { minY: number; frames: number[]; ys: number[] }>();
    const contactAcc = new Map<string, { frames: number[]; dists: number[] }>();

    const hands: { actorId: string; side: string; bone: THREE.Object3D }[] = [];
    const feet: { actorId: string; side: string; bone: THREE.Object3D }[] = [];
    for (const a of skeletons) {
      for (const side of HAND_SIDES) {
        const hand = deps.sourceBone(a.id, `${side}hand`);
        if (hand) hands.push({ actorId: a.id, side, bone: hand });
        const foot = deps.sourceBone(a.id, `${side}foot`);
        if (foot) feet.push({ actorId: a.id, side, bone: foot });
      }
    }
    const contactPairs: {
      actorId: string;
      limb: string;
      hand: THREE.Object3D;
      targetActorId: string;
      targetBone: string;
      bone: THREE.Object3D;
    }[] = [];
    for (const h of hands) {
      for (const other of skeletons) {
        if (other.id === h.actorId) continue;
        for (const canonical of CONTACT_TARGET_BONES) {
          const bone = deps.sourceBone(other.id, canonical);
          if (bone) {
            contactPairs.push({
              actorId: h.actorId,
              limb: `${h.side}hand`,
              hand: h.bone,
              targetActorId: other.id,
              targetBone: canonical,
              bone,
            });
          }
        }
      }
    }

    const posA = new THREE.Vector3();
    const posB = new THREE.Vector3();
    const quat = new THREE.Quaternion();

    deps.loop.stop();
    try {
      for (let frame = start; frame <= end; frame += stride) {
        deps.setMixersTime(frame / fps);

        // Fingers (healer detection, report-only).
        if (healer) {
          for (const anomaly of healer.detect()) {
            const key = `${anomaly.actorId}|${anomaly.side}|${anomaly.finger}`;
            const entry = fingerFrames.get(key) ?? { frames: [], confs: [] };
            entry.frames.push(frame);
            entry.confs.push(anomaly.confidence);
            fingerFrames.set(key, entry);
          }
        }

        // Hand flips: rotation step vs the previous sample.
        for (const h of hands) {
          h.bone.getWorldQuaternion(quat);
          const key = `${h.actorId}|${h.side}`;
          const prev = handQuats.get(key);
          if (prev) {
            const delta = (2 * Math.acos(Math.min(Math.abs(prev.dot(quat)), 1)) * 180) / Math.PI;
            if (delta > FLIP_THRESHOLD_DEG) {
              const last = flips[flips.length - 1];
              // Group events within a few frames of each other.
              if (
                !last ||
                last.actorId !== h.actorId ||
                last.side !== h.side ||
                frame - last.frame > stride * 4
              ) {
                flips.push({
                  actorId: h.actorId,
                  side: h.side,
                  frame,
                  deltaDegrees: +delta.toFixed(1),
                  suggestion:
                    'Probable tracker flip: key handFlip/handTurn (Finger tuning) around this frame.',
                });
              }
            }
            prev.copy(quat);
          } else {
            handQuats.set(key, quat.clone());
          }
        }

        // Ground penetration.
        for (const f of feet) {
          f.bone.getWorldPosition(posA);
          const key = `${f.actorId}|${f.side}`;
          const entry = groundAcc.get(key) ?? { minY: Infinity, frames: [], ys: [] };
          entry.minY = Math.min(entry.minY, posA.y);
          if (posA.y < GROUND_EPSILON) {
            entry.frames.push(frame);
            entry.ys.push(posA.y);
          }
          groundAcc.set(key, entry);
        }

        // Contact candidates.
        for (const pair of contactPairs) {
          pair.hand.getWorldPosition(posA);
          pair.bone.getWorldPosition(posB);
          const d = posA.distanceTo(posB);
          if (d < CONTACT_DISTANCE) {
            const key = `${pair.actorId}|${pair.limb}|${pair.targetActorId}|${pair.targetBone}`;
            const entry = contactAcc.get(key) ?? { frames: [], dists: [] };
            entry.frames.push(frame);
            entry.dists.push(d);
            contactAcc.set(key, entry);
          }
        }
      }
    } finally {
      deps.setMixersTime(state.currentFrame / fps);
      deps.loop.start();
    }

    /** Merge sampled frames into contiguous ranges (gap tolerance 4×stride). */
    const toRanges = (frames: number[], values: number[], pick: (v: number[]) => number) => {
      const ranges: { startFrame: number; endFrame: number; value: number }[] = [];
      let rangeStart = -1;
      let bucket: number[] = [];
      for (let i = 0; i < frames.length; i++) {
        if (rangeStart < 0) {
          rangeStart = frames[i];
          bucket = [values[i]];
        } else if (frames[i] - frames[i - 1] > stride * 4) {
          ranges.push({ startFrame: rangeStart, endFrame: frames[i - 1], value: pick(bucket) });
          rangeStart = frames[i];
          bucket = [values[i]];
        } else {
          bucket.push(values[i]);
        }
      }
      if (rangeStart >= 0) {
        ranges.push({
          startFrame: rangeStart,
          endFrame: frames[frames.length - 1],
          value: pick(bucket),
        });
      }
      return ranges;
    };

    const fingers: AnalyzeReport['fingers'] = [];
    for (const [key, entry] of fingerFrames) {
      const [actorId, side, finger] = key.split('|');
      fingers.push({
        actorId,
        side,
        finger,
        ranges: toRanges(entry.frames, entry.confs, (v) => Math.max(...v)).map((r) => ({
          startFrame: r.startFrame,
          endFrame: r.endFrame,
          peakConfidence: +r.value.toFixed(2),
        })),
        suggestion:
          'Raise "Fix broken fingers" strength, or key a finger tune over these ranges.',
      });
    }

    const ground: AnalyzeReport['ground'] = [];
    for (const [key, entry] of groundAcc) {
      const [actorId, side] = key.split('|');
      if (entry.frames.length === 0) continue;
      ground.push({
        actorId,
        side,
        minY: +entry.minY.toFixed(3),
        penetration: toRanges(entry.frames, entry.ys, (v) => Math.min(...v)).map((r) => ({
          startFrame: r.startFrame,
          endFrame: r.endFrame,
          minY: +r.value.toFixed(3),
        })),
        suggestion: 'Pin the foot to a floor point over these ranges, or raise the actor offset Y.',
      });
    }

    const contactCandidates: AnalyzeReport['contactCandidates'] = [];
    const minSamples = Math.max(2, Math.round((CONTACT_MIN_SECONDS * fps) / stride));
    for (const [key, entry] of contactAcc) {
      const [actorId, limb, targetActorId, targetBone] = key.split('|');
      for (const r of toRanges(entry.frames, entry.dists, (v) => Math.min(...v))) {
        if ((r.endFrame - r.startFrame) / stride + 1 < minSamples) continue;
        contactCandidates.push({
          actorId,
          limb,
          targetActorId,
          targetBone,
          startFrame: r.startFrame,
          endFrame: r.endFrame,
          minDistance: +r.value.toFixed(3),
          suggestion:
            'Probable intended contact: add a pin to this bone with influence keyed over the range.',
        });
      }
    }

    return {
      scanned: { startFrame: start, endFrame: end, stride },
      fingers,
      handFlips: flips,
      ground,
      contactCandidates,
    };
  }

  return { analyze };
}
